/*!
 * Amatora Shop JS
 * Handlers globales del theme: drawers, modals, qty, cart updates, predictive search.
 * No depende de jQuery ni librerías externas.
 *
 * Patrón: cada bloque registra sus listeners con event delegation desde document.
 * Dispara eventos custom al document para que distintos componentes se comuniquen
 * sin acoplarse:
 *   amatora:cart:add        (detail: { id, quantity })
 *   amatora:cart:updated    (detail: { cart })
 *   amatora:drawer:open     (detail: { id })
 *   amatora:drawer:close    (detail: { id })
 */
(function (global) {
  'use strict';

  // ═══════════════════════════════════════════════════════════
  // UTIL
  // ═══════════════════════════════════════════════════════════
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function moneyFormat(cents) {
    var fmt = (global.Shopify && global.Shopify.money_format) || '${{amount}}';
    cents = parseInt(cents, 10) || 0;
    var amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  // Igual que el filtro money_without_trailing_zeros: «$ 1,019.15», «$ 699»
  function moneyNoZeros(cents) {
    var fmt = (global.Shopify && global.Shopify.money_format) || '${{amount}}';
    var n = (parseInt(cents, 10) || 0) / 100;
    var parts = n.toFixed(2).split('.');
    var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts[1] === '00' ? int : int + '.' + parts[1];
    var amountComma = parts[1] === '00' ? int.replace(/,/g, '.') : int.replace(/,/g, '.') + ',' + parts[1];
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, int)
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  // Precio de la ficha (bloque Precio y, si está prendido, el de debajo del botón) con la
  // variante elegida. Mismo marcado que snippets/amatora-price.liquid.
  function updatePrices(form, v) {
    var section = form.closest('.main-product-am') || document;
    var targets = $$('[data-main-price] .amatora-price', section).concat($$('[data-product-price] .amatora-price', form));
    var onSale = v.compare_at_price && v.compare_at_price > v.price;
    targets.forEach(function (box) {
      var current = box.querySelector('.amatora-price__current');
      if (current) current.innerHTML = moneyNoZeros(v.price);
      var compare = box.querySelector('.amatora-price__compare');
      var save = box.querySelector('.amatora-price__save');
      if (onSale) {
        box.setAttribute('data-on-sale', 'true');
        if (!compare) { compare = document.createElement('span'); compare.className = 'amatora-price__compare'; current.after(compare); }
        compare.innerHTML = moneyNoZeros(v.compare_at_price);
        if (!save && box.closest('[data-main-price]')) { save = document.createElement('span'); save.className = 'amatora-price__save'; compare.after(save); }
        if (save) save.textContent = '−' + Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '%';
      } else {
        box.removeAttribute('data-on-sale');
        if (compare) compare.remove();
        if (save) save.remove();
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  // DRAWERS / MODALS — patrón <dialog> + open/close por trigger
  //
  // Markup esperado:
  //   <dialog id="cart-drawer" class="amatora-drawer">…</dialog>
  //   <button data-amatora-open="cart-drawer">…</button>
  //   <button data-amatora-close>…</button>  (dentro del dialog)
  //
  // UX:
  //   - Preserva scroll position al abrir/cerrar (no jump en iOS)
  //   - Auto-focus en primer input/link al abrir (search modal especialmente)
  //   - Cierre por click fuera del content
  //   - Cierre por ESC (nativo de <dialog> + cleanup de scroll lock)
  //   - Swipe horizontal (touch) cierra drawers laterales
  // ═══════════════════════════════════════════════════════════
  var savedScrollY = 0;

  function lockScroll() {
    if (document.documentElement.classList.contains('amatora-no-scroll')) return;
    savedScrollY = window.scrollY;
    document.documentElement.classList.add('amatora-no-scroll');
    // Compensa scrollbar para que no saltee el layout en desktop
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) document.documentElement.style.setProperty('--am-sbw', sbw + 'px');
    document.body.style.top = -savedScrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    if (!document.documentElement.classList.contains('amatora-no-scroll')) return;
    document.documentElement.classList.remove('amatora-no-scroll');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.removeProperty('--am-sbw');
    window.scrollTo(0, savedScrollY);
  }

  // ─ Keyframes según variante del drawer ─
  // Sin scale ni overshoot exagerado: solo translate limpio.
  function getDrawerKeyframes(dlg, dir /* 'in' | 'out' */) {
    var isLeft = dlg.classList.contains('amatora-drawer--left');
    var isCenter = dlg.classList.contains('amatora-drawer--center');
    var isBottom = dlg.classList.contains('amatora-drawer--bottom');
    var hidden, visible;

    if (isBottom) {
      hidden  = { transform: 'translateY(100%)' };
      visible = { transform: 'translateY(0)' };
    } else if (isCenter) {
      hidden  = { transform: 'translateY(16px) scale(0.97)', opacity: 0 };
      visible = { transform: 'translateY(0) scale(1)',       opacity: 1 };
    } else if (isLeft) {
      hidden  = { transform: 'translateX(-100%)' };
      visible = { transform: 'translateX(0)' };
    } else {
      hidden  = { transform: 'translateX(100%)' };
      visible = { transform: 'translateX(0)' };
    }
    return dir === 'in' ? [hidden, visible] : [visible, hidden];
  }

  var ANIM_OPEN = {
    duration: 340,
    easing:   'cubic-bezier(0.22, 1, 0.36, 1)',   // ease-out-quint (premium)
    fill:     'both'
  };
  var ANIM_CLOSE = {
    duration: 240,
    easing:   'cubic-bezier(0.55, 0, 1, 0.45)',   // ease-in-quad (cerrar rápido)
    fill:     'both'
  };

  function openDialog(id) {
    var dlg = document.getElementById(id);
    if (!dlg) return;
    if (typeof dlg.showModal !== 'function' || dlg.open) return;

    dlg.classList.remove('is-open');
    dlg.showModal();

    var content = dlg.querySelector('.amatora-drawer__content');

    // Web Animations API — reliable en todos los browsers modernos.
    // Cancela animations previas para que no se acumulen al abrir/cerrar
    // rápidamente.
    if (content && content.animate) {
      if (content.getAnimations) {
        content.getAnimations().forEach(function (a) { a.cancel(); });
      }
      content.animate(getDrawerKeyframes(dlg, 'in'), ANIM_OPEN);
    }

    // .is-open dispara animaciones de items/header/footer/backdrop vía CSS.
    requestAnimationFrame(function () {
      dlg.classList.add('is-open');
    });

    lockScroll();
    autofocusDialog(dlg);
    emit('amatora:drawer:open', { id: id });
  }

  function closeDialog(dlg) {
    if (!dlg || !dlg.open) return;

    var content = dlg.querySelector('.amatora-drawer__content');
    dlg.classList.remove('is-open'); // dispara backdrop fade-out + items reset

    function finish() {
      if (typeof dlg.close === 'function') dlg.close();
      unlockScroll();
      emit('amatora:drawer:close', { id: dlg.id });
    }

    if (content && content.animate) {
      if (content.getAnimations) {
        content.getAnimations().forEach(function (a) { a.cancel(); });
      }
      var anim = content.animate(getDrawerKeyframes(dlg, 'out'), ANIM_CLOSE);
      anim.onfinish = finish;
      // Safety net por si onfinish no dispara
      setTimeout(function () { if (dlg.open) finish(); }, ANIM_CLOSE.duration + 100);
    } else {
      finish();
    }
  }

  function autofocusDialog(dlg) {
    // Pequeño delay para que el dialog termine de aparecer y el browser
    // no robe foco al close button automáticamente.
    setTimeout(function () {
      var target = dlg.querySelector('[data-autofocus], input[type="search"], input[type="email"]:not([readonly])');
      if (target) target.focus({ preventScroll: true });
    }, 80);
  }

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-amatora-open]');
    if (openBtn) {
      e.preventDefault();
      openDialog(openBtn.getAttribute('data-amatora-open'));
      return;
    }
    var closeBtn = e.target.closest('[data-amatora-close]');
    if (closeBtn) {
      var dlg = closeBtn.closest('dialog');
      if (dlg) closeDialog(dlg);
      return;
    }
  });

  // Click fuera del content → cerrar
  document.addEventListener('click', function (e) {
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('amatora-drawer')) {
      var content = e.target.querySelector('.amatora-drawer__content');
      if (!content) return;
      var c = content.getBoundingClientRect();
      var inside = e.clientX >= c.left && e.clientX <= c.right && e.clientY >= c.top && e.clientY <= c.bottom;
      if (!inside) closeDialog(e.target);
    }
  });

  // ESC: interceptamos el cancel event para cerrar CON animación
  // (sino el dialog se cierra instantáneo cuando user presiona ESC).
  document.addEventListener('cancel', function (e) {
    if (!e.target.classList || !e.target.classList.contains('amatora-drawer')) return;
    e.preventDefault();
    closeDialog(e.target);
  }, true);

  // ─ Swipe horizontal para cerrar drawers laterales (touch) ─
  // Drawer derecho (cart): swipe a la derecha cierra
  // Drawer izquierdo (mobile menu): swipe a la izquierda cierra
  (function () {
    var startX = 0, startY = 0, currentDlg = null, isLeft = false;

    document.addEventListener('touchstart', function (e) {
      var content = e.target.closest('.amatora-drawer__content');
      if (!content) return;
      var dlg = content.parentElement;
      // Solo drawers laterales (no el modal centrado)
      if (dlg.classList.contains('amatora-drawer--center')) return;
      currentDlg = dlg;
      isLeft = dlg.classList.contains('amatora-drawer--left');
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      if (!currentDlg) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      // Solo arrastrar si gesto es claramente horizontal
      if (Math.abs(dy) > Math.abs(dx)) return;
      // Resistencia + dirección correcta
      var move = isLeft ? Math.min(0, dx) : Math.max(0, dx);
      var content = currentDlg.querySelector('.amatora-drawer__content');
      if (content) {
        content.style.transform = 'translateX(' + move + 'px)';
        content.style.transition = 'none';
      }
    }, { passive: true });

    document.addEventListener('touchend', function (e) {
      if (!currentDlg) return;
      var dx = e.changedTouches[0].clientX - startX;
      var content = currentDlg.querySelector('.amatora-drawer__content');
      if (content) {
        content.style.transform = '';
        content.style.transition = '';
      }
      var threshold = (currentDlg.querySelector('.amatora-drawer__content').offsetWidth || 320) * 0.3;
      var shouldClose = isLeft ? dx < -threshold : dx > threshold;
      if (shouldClose) closeDialog(currentDlg);
      currentDlg = null;
    });
  })();

  // ─ Mobile menu: submenu accordion ─
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-submenu-toggle]');
    if (!btn) return;
    var id = btn.getAttribute('data-submenu-toggle');
    var panel = document.getElementById(id);
    if (!panel) return;
    var open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    panel.setAttribute('data-open', String(!open));
  });

  // ═══════════════════════════════════════════════════════════
  // QTY INPUT — botones +/- que actualizan input[type=number]
  // Markup esperado:
  //   <div data-amatora-qty>
  //     <button data-qty-action="dec">–</button>
  //     <input type="number" min="1" value="1">
  //     <button data-qty-action="inc">+</button>
  //   </div>
  //
  // Eventos disparados al cambiar valor: 'input' + 'change' (ambos bubble).
  // Importante: nunca usar `parseInt(x) || fallback` porque 0 es falsy.
  // ═══════════════════════════════════════════════════════════
  function _parseAttr(input, attr, fallback) {
    var s = input.getAttribute(attr);
    if (s === null || s === '') return fallback;
    var n = parseInt(s, 10);
    return isNaN(n) ? fallback : n;
  }
  function _parseValue(input, fallback) {
    var n = parseInt(input.value, 10);
    return isNaN(n) ? fallback : n;
  }
  function _setQty(input, v) {
    input.value = v;
    input.dispatchEvent(new Event('input',  { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-action]');
    if (!btn) return;
    var wrap = btn.closest('[data-amatora-qty]');
    if (!wrap) return;
    var input = wrap.querySelector('input[type="number"]');
    if (!input) return;

    var min = _parseAttr(input, 'min', 1);
    var max = _parseAttr(input, 'max', 999);
    var v   = _parseValue(input, min);

    if (btn.getAttribute('data-qty-action') === 'inc') v = Math.min(v + 1, max);
    else                                               v = Math.max(v - 1, min);

    _setQty(input, v);
  });

  // Sanitiza el qty al perder foco: vacío / NaN / fuera de rango → ajusta.
  document.addEventListener('blur', function (e) {
    if (!e.target.matches) return;
    if (!e.target.matches('[data-amatora-qty] input[type="number"]')) return;
    var input = e.target;
    var min = _parseAttr(input, 'min', 1);
    var max = _parseAttr(input, 'max', 999);
    var v   = _parseValue(input, NaN);
    if (isNaN(v))  { _setQty(input, min); return; }
    if (v < min)   { _setQty(input, min); return; }
    if (v > max)   { _setQty(input, max); return; }
  }, true);

  // ═══════════════════════════════════════════════════════════
  // ADD TO CART — el AJAX vive en amatora-add-to-cart.liquid (script global).
  // Aquí solo escuchamos el evento que emite ese script para:
  //   1) refrescar el cart drawer
  //   2) abrir el drawer si existe
  //   3) sincronizar el contador del header
  // El drawer de este theme es un <dialog>, no el <cart-drawer> de Dawn, así que
  // el snippet del sistema solo emite el evento y la apertura vive aquí.
  // Respeta "Abrir el carrito al agregar" del panel Configuraciones Amatora.
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('amatora:cart:added', function () {
    var cfg = global.AmatoraConfig || {};
    refreshCart().then(function () {
      if (cfg.cartOpenDrawer !== false && document.getElementById('cart-drawer')) openDialog('cart-drawer');
    });
  });

  // ═══════════════════════════════════════════════════════════
  // GALERÍA EN CARRUSEL — contador «2/6». El evento del slider no burbujea,
  // así que se escucha en cada carrusel de galería.
  // ═══════════════════════════════════════════════════════════
  function bindGalleryCounters() {
    $$('[data-gallery-slider]').forEach(function (g) {
      if (g.dataset.counterReady) return;
      var sl = g.querySelector('[data-amatora-slider]');
      var out = g.querySelector('[data-gallery-current]');
      if (!sl || !out) return;
      g.dataset.counterReady = '1';
      sl.addEventListener('slider-amatora:change', function (e) {
        out.textContent = (e.detail && typeof e.detail.index === 'number' ? e.detail.index : 0) + 1;
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindGalleryCounters);
  else bindGalleryCounters();

  // ═══════════════════════════════════════════════════════════
  // CARD · FOTOS EN CARRUSEL (ajuste card_gallery)
  // Hay ~20 cards por página. Si salieran con [data-amatora-slider], amatora.js
  // armaría los 20 sliders al cargar (DOM + medidas + ResizeObserver cada uno).
  // Por eso salen con [data-card-gallery] y cada una se vuelve slider cuando se
  // acerca a la pantalla (IntersectionObserver) o cuando el usuario la toca o la
  // enfoca. Las cards que llegan después (colección «ver más», recomendaciones,
  // customizer) las detecta un MutationObserver.
  // ═══════════════════════════════════════════════════════════
  var cardGalleryIO = null;
  function activateCardGallery(el) {
    if (!el || el.dataset.cardGalleryOn) return;
    if (!global.SliderAmatora || typeof global.SliderAmatora.initAll !== 'function') return;
    el.dataset.cardGalleryOn = '1';
    if (cardGalleryIO) cardGalleryIO.unobserve(el);
    // Dentro de un carrusel de cards (relacionados, colección destacada) el
    // arrastre sobre la foto mueve las fotos y no el carrusel de afuera.
    ['mousedown', 'touchstart', 'touchmove', 'touchend'].forEach(function (type) {
      el.addEventListener(type, function (e) {
        if (!el.classList.contains('is-static')) e.stopPropagation();
      }, type === 'mousedown' ? false : { passive: true });
    });
    el.setAttribute('data-amatora-slider', '');
    global.SliderAmatora.initAll(el.parentNode);
  }
  function scanCardGalleries(root) {
    $$('[data-card-gallery]:not([data-card-gallery-seen])', root).forEach(function (el) {
      el.setAttribute('data-card-gallery-seen', '');
      if (cardGalleryIO) cardGalleryIO.observe(el);
      else activateCardGallery(el);
    });
  }
  if ('IntersectionObserver' in global) {
    cardGalleryIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) activateCardGallery(en.target); });
    }, { rootMargin: '200px 0px' });
  }
  ['pointerover', 'focusin', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var g = e.target && e.target.closest && e.target.closest('[data-card-gallery]');
      if (g) activateCardGallery(g);
    }, { passive: true });
  });
  if ('MutationObserver' in global) {
    var cardScanQueued = false;
    new MutationObserver(function (muts) {
      if (cardScanQueued) return;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.hasAttribute('data-card-gallery') || n.querySelector('[data-card-gallery]'))) {
            cardScanQueued = true;
            requestAnimationFrame(function () { cardScanQueued = false; scanCardGalleries(document); });
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scanCardGalleries(document); });
  else scanCardGalleries(document);

  // ═══════════════════════════════════════════════════════════
  // CARD: bolitas de color → muestra las tallas de ese color y su foto
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('click', function (e) {
    var sw = e.target.closest('.amatora-card-product__swatch');
    if (!sw) return;
    var wrap = sw.closest('[data-card-colors]');
    var card = sw.closest('.amatora-card-product');
    if (!wrap || !card) return;
    $$('.amatora-card-product__swatch', wrap).forEach(function (b) { b.setAttribute('aria-checked', b === sw ? 'true' : 'false'); });
    $$('[data-color-group]', wrap).forEach(function (g) { g.hidden = g.getAttribute('data-color-group') !== sw.getAttribute('data-color'); });
    var idx = sw.getAttribute('data-media-index');
    var gal = card.querySelector('[data-card-gallery]');
    var first = card.querySelector('.amatora-card-product__image');
    // Se guarda la primera foto original para poder regresar a ella
    if (first && !first.hasAttribute('data-orig-src')) {
      first.setAttribute('data-orig-src', first.getAttribute('src') || '');
      first.setAttribute('data-orig-srcset', first.getAttribute('srcset') || '');
    }
    if (idx === null && sw.getAttribute('data-img')) {
      // La foto del color no está entre las primeras de la tarjeta: se pone en la primera foto.
      if (first) {
        first.setAttribute('srcset', sw.getAttribute('data-srcset') || '');
        first.setAttribute('src', sw.getAttribute('data-img'));
      }
      idx = 0;
    } else if (first && first.getAttribute('src') !== first.getAttribute('data-orig-src')) {
      // Volver a un color cuya foto sí está en la tarjeta: se regresa la primera foto original
      first.setAttribute('srcset', first.getAttribute('data-orig-srcset'));
      first.setAttribute('src', first.getAttribute('data-orig-src'));
    }
    if (idx === null) return;
    idx = Number(idx);
    var inst = gal && global.SliderAmatora && global.SliderAmatora.get && global.SliderAmatora.get(gal);
    if (!inst && gal && global.SliderAmatora && global.SliderAmatora.initAll) { global.SliderAmatora.initAll(gal.parentElement); inst = global.SliderAmatora.get(gal); }
    if (inst && typeof inst._snap === 'function') inst._snap(idx, true);
  });

  // ═══════════════════════════════════════════════════════════
  // VIDEOS DE FONDO [data-bg-video] — se piden al acercarse a la pantalla,
  // se pausan al salir o con la pestaña oculta; con «reducir movimiento» no se
  // cargan (se queda la portada).
  // ═══════════════════════════════════════════════════════════
  (function () {
    var reduce = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in global)) return;
    var mobile = global.matchMedia('(max-width: 767px)').matches;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (!v.getAttribute('src')) {
            v.setAttribute('src', (mobile && v.getAttribute('data-src-mobile')) || v.getAttribute('data-src'));
            v.addEventListener('playing', function () { v.classList.add('is-playing'); }, { once: true });
          }
          var p = v.play(); if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) v.pause();
      });
    }, { rootMargin: '200px 0px' });
    function bind(scope) { $$('[data-bg-video]', scope).forEach(function (v) { if (!v.dataset.bgReady) { v.dataset.bgReady = '1'; io.observe(v); } }); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { bind(); }); else bind();
    document.addEventListener('shopify:section:load', function (e) { bind(e.target); });
    document.addEventListener('visibilitychange', function () {
      $$('[data-bg-video]').forEach(function (v) { if (document.hidden) v.pause(); });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // PESTAÑAS DENTRO DE UN PANEL (ej. Lavado / Secado / Planchado)
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-sheet-tabs] [role="tab"]');
    if (!tab) return;
    var wrap = tab.closest('[data-sheet-tabs]');
    $$('[role="tab"]', wrap).forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  });

  // ═══════════════════════════════════════════════════════════
  // PRODUCT FORM — variant picker
  // Al cambiar un radio de [data-option-input], busca el variant que matchea
  // y actualiza data-variant-id del [data-add-to-cart], precio y disponibilidad.
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-option-input]');
    if (!input) return;
    var form = input.closest('[data-product-form]');
    if (!form) return;
    syncVariant(form);
    refreshPending(form);
  });

  // ── Sin elegir todo: el botón avisa qué falta ──
  // Con «No elegir talla por defecto» el botón no está deshabilitado (aria-disabled): al tocarlo
  // sin elegir color/talla dice qué falta debajo y marca esas opciones en rojo.
  function missingOptions(form) {
    return Array.prototype.filter.call(form.querySelectorAll('[data-option-position]'), function (fs) {
      return !fs.hidden && !fs.querySelector('[data-option-input]:checked');
    });
  }
  function joinNames(fields) {
    var n = fields.map(function (fs) { return fs.getAttribute('data-option-name') || ''; });
    if (n.length < 2) return n.join('');
    return n.slice(0, -1).join(', ') + ' y ' + n[n.length - 1];
  }
  function refreshPending(form) {
    var btn = form.querySelector('[data-add-to-cart]');
    if (!btn || !btn.hasAttribute('data-needs-options')) return;
    var missing = missingOptions(form);
    var hint = form.querySelector('[data-options-hint]');
    form.querySelectorAll('[data-option-position].is-missing').forEach(function (fs) {
      if (missing.indexOf(fs) === -1) fs.classList.remove('is-missing');
    });
    if (!missing.length) { if (hint) hint.hidden = true; return; }
    var names = joinNames(missing);
    var label = btn.querySelector('.btn-label');
    var tpl = form.getAttribute('data-choose-template') || '';
    if (label && tpl.indexOf('[opciones]') !== -1) label.textContent = tpl.replace('[opciones]', names);
    if (hint && !hint.hidden) hint.textContent = (form.getAttribute('data-missing-template') || '').replace('[opciones]', names);
  }
  // En captura: corre antes que el agregar al carrito (amatora-add-to-cart.liquid)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-to-cart][data-needs-options]');
    if (!btn) return;
    var form = btn.closest('[data-product-form]');
    if (!form) return;
    var missing = missingOptions(form);
    if (!missing.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var hint = form.querySelector('[data-options-hint]');
    if (hint) {
      hint.textContent = (form.getAttribute('data-missing-template') || '').replace('[opciones]', joinNames(missing));
      hint.hidden = false;
    }
    missing.forEach(function (fs) {
      fs.classList.remove('is-missing');
      void fs.offsetWidth; // reinicia la sacudida si se vuelve a tocar
      fs.classList.add('is-missing');
    });
    var r = missing[0].getBoundingClientRect();
    if (r.top < 80 || r.bottom > global.innerHeight) missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, true);

  function syncVariant(form) {
    var json = form.querySelector('[data-variants-json]');
    if (!json) return;
    var variants;
    try { variants = JSON.parse(json.textContent); } catch (e) { return; }
    // Un valor por opción en su posición (undefined si esa opción no se ha elegido),
    // para que «sin talla elegida» no recorra los valores de las demás opciones.
    var values = Array.prototype.map.call(form.querySelectorAll('[data-option-position]'), function (fs) {
      var r = fs.querySelector('[data-option-input]:checked');
      return r ? r.value : undefined;
    });

    var matched = variants.find(function (v) {
      return v.options.every(function (opt, i) { return opt === values[i]; });
    });

    var btn = form.querySelector('[data-add-to-cart]');
    if (btn && matched) {
      form.removeAttribute('data-pending');
      btn.removeAttribute('data-needs-options');
      var hintDone = form.querySelector('[data-options-hint]');
      if (hintDone) hintDone.hidden = true;
      form.querySelectorAll('[data-option-position].is-missing').forEach(function (fs) { fs.classList.remove('is-missing'); });
      btn.setAttribute('data-variant-id', matched.id);
      updatePrices(form, matched);
      if (matched.available) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        var label = btn.querySelector('.btn-label');
        // data-label-add: el texto de agregar cuando el botón arrancó en «Elige tu talla»
        if (label && btn.dataset.labelAdd) label.textContent = btn.dataset.labelAdd;
        else if (label && label.dataset.originalText) label.textContent = label.dataset.originalText;
      } else {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        var label2 = btn.querySelector('.btn-label');
        if (label2) {
          if (!label2.dataset.originalText) label2.dataset.originalText = label2.textContent;
          label2.textContent = (global.amatoraStrings && global.amatoraStrings.soldOut) || 'Agotado';
        }
      }
    }

    // Etiqueta del valor seleccionado en cada fieldset + active state
    form.querySelectorAll('[data-option-position]').forEach(function (fs, i) {
      var label = fs.querySelector('[data-selected-value]');
      if (label) label.textContent = values[i] || label.getAttribute('data-placeholder') || '';
      fs.querySelectorAll('.amatora-variant__option').forEach(function (opt) {
        var radio = opt.querySelector('[data-option-input]');
        opt.classList.toggle('is-active', radio && radio.checked);
      });
    });

    // Disponibilidad dinámica de cada option value según el resto de selecciones.
    updateOptionAvailability(form, variants, values);

    // Galería: oculta media de otras variantes, muestra la de la actual.
    if (matched) updateGalleryForVariant(form, matched.id);

    // Pulse sutil en el price y la galería cuando cambia variante
    pulseVariantFeedback(form);
  }

  // Pulse sutil para indicar que algo cambió por selección de variante.
  function pulseVariantFeedback(form) {
    var section = form.closest('.main-product-am') || document;
    var targets = [
      form.querySelector('[data-product-price]'),
      section.querySelector('[data-product-gallery] [data-media-main]:not([hidden])')
    ].filter(Boolean);

    targets.forEach(function (el) {
      if (!el || !el.animate) return;
      el.animate(
        [
          { opacity: 0.7, transform: 'scale(0.99)' },
          { opacity: 1,   transform: 'scale(1)' }
        ],
        { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    });
  }

  // Para cada option value: ¿existe alguna variant in-stock con ese valor +
  // las selecciones actuales en las OTRAS opciones? Si no, marca is-unavailable.
  function updateOptionAvailability(form, variants, currentValues) {
    var fieldsets = form.querySelectorAll('[data-option-position]');
    fieldsets.forEach(function (fs, fsIdx) {
      fs.querySelectorAll('.amatora-variant__option').forEach(function (opt) {
        var radio = opt.querySelector('[data-option-input]');
        if (!radio) return;
        var thisValue = radio.value;
        var available = variants.some(function (v) {
          if (!v.available) return false;
          if (v.options[fsIdx] !== thisValue) return false;
          for (var i = 0; i < currentValues.length; i++) {
            if (i === fsIdx) continue;
            if (currentValues[i] !== undefined && v.options[i] !== currentValues[i]) return false;
          }
          return true;
        });
        opt.classList.toggle('is-unavailable', !available);
      });
    });
  }

  // Galería: oculta variant-linked media excepto la del variant actual.
  // General media (sin data-variant-media) no se toca — siempre visible la primera.
  function updateGalleryForVariant(form, variantId) {
    var section = form.closest('.main-product-am') || document;
    var gallery = section.querySelector('[data-product-gallery]');
    if (!gallery) return;

    var mainItems = gallery.querySelectorAll('[data-media-main]');
    var thumbItems = gallery.querySelectorAll('[data-media-thumb]');
    var variantStr = String(variantId);
    var variantMain = null;
    var variantMainIdx = null;

    // Busca el main asociado al variant actual
    mainItems.forEach(function (el) {
      var linked = el.getAttribute('data-variant-media');
      if (linked === variantStr) {
        variantMain = el;
        variantMainIdx = el.getAttribute('data-media-main');
      }
    });

    // Show/hide mains: el variant-linked actual visible; otros variant-linked hidden;
    // generales: el actual hidden si hay variant media, sino el primero visible.
    mainItems.forEach(function (el) {
      var linked = el.getAttribute('data-variant-media');
      if (linked) {
        el.hidden = linked !== variantStr;
      } else {
        // General media: si hay variant main, ocúltalo; sino mantén estado.
        if (variantMain) {
          el.hidden = true;
        }
      }
    });

    // Si no había variant media, asegura que al menos uno general esté visible (el primero).
    if (!variantMain) {
      var firstGeneral = gallery.querySelector('[data-media-main]:not([data-variant-media])');
      if (firstGeneral) firstGeneral.hidden = false;
    }

    // Thumbs: idem
    thumbItems.forEach(function (el) {
      var linked = el.getAttribute('data-variant-thumb');
      if (linked) {
        el.hidden = linked !== variantStr;
      } else {
        el.hidden = false;
      }
      el.classList.remove('is-active');
    });

    // Marca el thumb actual como active
    if (variantMainIdx) {
      var activeThumb = gallery.querySelector('[data-media-thumb="' + variantMainIdx + '"]');
      if (activeThumb) activeThumb.classList.add('is-active');
    } else {
      var firstThumb = gallery.querySelector('[data-media-thumb]:not([hidden])');
      if (firstThumb) firstThumb.classList.add('is-active');
    }
  }

  // Sincroniza qty del input con data-quantity del botón (para que el script
  // global de add-to-cart lea la qty actualizada). Mínimo 1 — el botón
  // siempre agrega al menos 1 unidad.
  document.addEventListener('input', function (e) {
    var qty = e.target.closest('[data-add-to-cart-qty]');
    if (!qty) return;
    var form = qty.closest('[data-product-form]');
    if (!form) return;
    var btn = form.querySelector('[data-add-to-cart]');
    if (!btn) return;
    var n = _parseValue(qty, 1);
    if (n < 1) n = 1;
    btn.setAttribute('data-quantity', String(n));
  });

  // ═══════════════════════════════════════════════════════════
  // CART API — refresh, change qty, remove line
  // ═══════════════════════════════════════════════════════════
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        emit('amatora:cart:updated', { cart: cart });
        renderCartDrawer();
        return cart;
      });
  }

  function updateCartCount(n) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.toggleAttribute('hidden', n === 0);
    });
  }

  function renderCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var sectionId = drawer.getAttribute('data-section-id');
    if (!sectionId) return;
    // Con el prefijo del idioma (/es/): sin él Shopify pinta el drawer en el idioma principal.
    var root = ((global.routes && global.routes.root_url) || '/').replace(/\/?$/, '/');
    return fetch(root + '?section_id=' + sectionId, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-content]');
        var current = drawer.querySelector('[data-cart-drawer-content]');
        if (!fresh || !current) return;

        // Crossfade: out → swap → in. No flash, no pop.
        if (current.animate) {
          current.animate(
            [{ opacity: 1 }, { opacity: 0 }],
            { duration: 140, easing: 'ease-out', fill: 'forwards' }
          ).onfinish = function () {
            current.replaceWith(fresh);
            if (drawer.open) {
              loadCartRecs(fresh);
              // El foco estaba en el header viejo: lo pasamos al botón cerrar nuevo.
              if (!drawer.contains(document.activeElement)) {
                var af = fresh.querySelector('[data-autofocus]');
                if (af) af.focus({ preventScroll: true });
              }
            }
            fresh.animate(
              [{ opacity: 0 }, { opacity: 1 }],
              { duration: 200, easing: 'ease-out', fill: 'both' }
            );
          };
        } else {
          current.replaceWith(fresh);
        }
      });
  }

  // Debounce por línea + estado visual is-updating mientras sync con server.
  // Click rápido +/+/+ → 1 sola llamada con el qty final. Sin race conditions.
  // Carrito lateral y /cart comparten esto: cada uno es un [data-cart-root] con
  // su data-section-id, y /cart/change.js los devuelve ya pintados (sections).
  var _cartLineTimers = {};
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-line-qty]');
    if (!input) return;
    var line = input.getAttribute('data-line');
    var qty = _parseValue(input, 1);
    if (qty < 0) qty = 0;

    // Marca la línea como "updating" — feedback visual inmediato
    var lineEl = input.closest('.amatora-cart-line');
    if (lineEl) setLineBusy(lineEl, true);

    clearTimeout(_cartLineTimers[line]);
    _cartLineTimers[line] = setTimeout(function () {
      changeCartLine(line, qty, lineEl);
    }, 250);
  });

  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-cart-line-remove]');
    if (!rm) return;
    e.preventDefault();
    var lineEl = rm.closest('.amatora-cart-line');
    if (lineEl) setLineBusy(lineEl, true);
    changeCartLine(rm.getAttribute('data-line'), 0, lineEl);
  });

  function setLineBusy(lineEl, on) {
    lineEl.classList.toggle('is-updating', on);
    lineEl.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  // Textos del JS: salen de data-str-* del carrito (locales/*.json), no fijos aquí.
  function cartStr(name) {
    var root = $('[data-cart-root]');
    return (root && root.getAttribute('data-str-' + name)) || '';
  }

  function announceCart(msg) {
    $$('[data-cart-live]').forEach(function (el) {
      el.textContent = '';
      setTimeout(function () { el.textContent = msg; }, 60);
    });
  }

  // Busca la línea por su key; si Shopify la cambió (ej. al entrar un descuento
  // automático la key cambia), cae al mismo número de línea.
  function findCartLine(scope, key, index) {
    var lines = $$('[data-line-key]', scope);
    var hit = lines.filter(function (l) { return l.getAttribute('data-line-key') === key; })[0];
    if (!hit && index) {
      hit = lines.filter(function (l) {
        var q = l.querySelector('[data-cart-line-qty]');
        return q && q.getAttribute('data-line') === String(index);
      })[0];
    }
    return hit || null;
  }

  function showLineError(key, msg, index) {
    if (!key || !msg) return;
    $$('[data-cart-root]').forEach(function (root) {
      var el = findCartLine(root, key, index);
      var box = el && el.querySelector('[data-cart-line-error]');
      if (box) { box.textContent = msg; box.hidden = false; }
    });
  }

  // Al re-pintar se pierde el foco: lo regresamos al mismo control de la misma línea.
  function captureCartFocus() {
    var a = document.activeElement;
    if (!a || !a.closest) return null;
    var root = a.closest('[data-cart-root]');
    if (!root) return null;
    var line = a.closest('[data-line-key]');
    var kind = null;
    ['[data-qty-action="inc"]', '[data-qty-action="dec"]', '[data-cart-line-qty]'].forEach(function (s) {
      if (a.matches(s)) kind = s;
    });
    var q = line && line.querySelector('[data-cart-line-qty]');
    return { el: a, root: root, key: line && line.getAttribute('data-line-key'), index: q && q.getAttribute('data-line'), kind: kind };
  }

  function restoreCartFocus(f) {
    if (!f || f.el.isConnected) return; // el control sigue en la página: no se toca
    var el = null;
    if (f.key && f.kind) {
      var line = findCartLine(f.root, f.key, f.index);
      if (line) {
        el = line.querySelector(f.kind);
        if (el && el.disabled) el = line.querySelector('[data-cart-line-qty]');
      }
    }
    if (!el) el = f.root.querySelector('.amatora-cart-line [data-cart-line-qty]') || f.root.querySelector('[data-autofocus]');
    if (el) el.focus({ preventScroll: true });
  }

  function swapCartSections(map) {
    if (!map) return;
    $$('[data-cart-root]').forEach(function (root) {
      var html = map[root.getAttribute('data-section-id')];
      if (!html) return;
      var sel = root.hasAttribute('data-main-cart') ? '[data-main-cart-content]' : '[data-cart-drawer-content]';
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
      var current = root.querySelector(sel);
      if (!fresh || !current) return;
      var noteOpen = !!current.querySelector('[data-cart-note-details][open]');
      // Sin la entrada escalonada de las líneas: solo cambió una cantidad.
      if (root.tagName === 'DIALOG') root.classList.add('is-settled');
      current.replaceWith(fresh);
      if (noteOpen) {
        var d = fresh.querySelector('[data-cart-note-details]');
        if (d) d.open = true;
      }
      if (root.tagName !== 'DIALOG' || root.open) loadCartRecs(fresh);
    });
  }

  function changeCartLine(line, qty, lineEl) {
    var key = lineEl ? lineEl.getAttribute('data-line-key') : null;
    var focus = captureCartFocus();
    var ids = $$('[data-cart-root]').map(function (r) { return r.getAttribute('data-section-id'); }).filter(Boolean);
    var url = ((global.routes && global.routes.cart_change_url) || '/cart/change') + '.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        line: parseInt(line, 10),
        quantity: qty,
        sections: ids,
        sections_url: window.location.pathname
      })
    })
    .then(function (r) {
      return r.json().then(function (json) { return { ok: r.ok, json: json }; });
    })
    .then(function (res) {
      // 422 de Shopify (sin inventario, etc.): { status, message, description }
      if (!res.ok || res.json.status) {
        throw new Error(res.json.description || res.json.message || '');
      }
      var cart = res.json;
      // Si Shopify recortó la cantidad al inventario, lo decimos en la línea.
      var msg = '';
      if (qty > 0 && key) {
        var item = (cart.items || []).filter(function (it) { return it.key === key; })[0] ||
                   (cart.items || [])[parseInt(line, 10) - 1];
        if (item && item.quantity < qty) msg = cartStr('max').replace('[n]', item.quantity);
      }
      updateCartCount(cart.item_count);
      if (cart.sections) swapCartSections(cart.sections);
      else renderCartDrawer();
      emit('amatora:cart:updated', { cart: cart });
      showLineError(key, msg, line);
      restoreCartFocus(focus);
      announceCart(msg || cartStr('updated'));
    })
    .catch(function (err) {
      var msg = (err && err.message) || cartStr('error');
      if (lineEl) {
        setLineBusy(lineEl, false);
        var input = lineEl.querySelector('[data-cart-line-qty]');
        if (input) input.value = input.defaultValue;
      }
      showLineError(key, msg, line);
      announceCart(msg);
    });
  }

  // Nota del pedido: se guarda sola (así no se pierde al re-pintar el carrito).
  var _cartNoteTimer;
  document.addEventListener('input', function (e) {
    var note = e.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(_cartNoteTimer);
    _cartNoteTimer = setTimeout(function () {
      fetch(((global.routes && global.routes.cart_update_url) || '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }, 500);
  });

  // «Complétalo con…»: recomendaciones de Shopify del primer producto del carrito
  // (sections/cart-recommendations.liquid). Una petición por producto y visita;
  // se quitan las que ya están en el carrito.
  var _cartRecs = {};
  function loadCartRecs(scope) {
    var box = scope && scope.querySelector('[data-cart-recs]');
    if (!box) return;
    var pid = box.getAttribute('data-product-id');
    var limit = parseInt(box.getAttribute('data-limit'), 10) || 3;
    var exclude = (box.getAttribute('data-exclude') || '').split(',');
    if (!_cartRecs[pid]) {
      _cartRecs[pid] = fetch(box.getAttribute('data-url') + '?section_id=cart-recommendations&intent=related&limit=' + (limit + 4) + '&product_id=' + pid)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }
    _cartRecs[pid].then(function (html) {
      if (!html || !box.isConnected) return;
      var list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recs-list]');
      if (!list) return;
      var shown = 0;
      $$('[data-product-id]', list).forEach(function (el) {
        if (shown >= limit || exclude.indexOf(el.getAttribute('data-product-id')) !== -1) el.remove();
        else shown++;
      });
      if (!shown) return;
      var slot = box.querySelector('[data-cart-recs-slot]');
      if (slot) slot.replaceChildren(list);
      box.hidden = false;
    });
  }

  document.addEventListener('amatora:drawer:open', function (e) {
    if (e.detail && e.detail.id === 'cart-drawer') loadCartRecs(document.getElementById('cart-drawer'));
  });
  document.addEventListener('amatora:drawer:close', function (e) {
    var d = e.detail && document.getElementById(e.detail.id);
    if (d) d.classList.remove('is-settled');
  });

  // En /cart: al agregar algo (ej. desde «Complétalo con») se re-pinta la página.
  document.addEventListener('amatora:cart:added', function () {
    var main = $('[data-main-cart]');
    if (!main) return;
    var id = main.getAttribute('data-section-id');
    fetch(window.location.pathname + '?section_id=' + encodeURIComponent(id), { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { var m = {}; m[id] = html; swapCartSections(m); });
  });
  document.addEventListener('DOMContentLoaded', function () {
    var main = $('[data-main-cart]');
    if (main) loadCartRecs(main);
  });

  // Sincronizar cart count al inicio
  document.addEventListener('DOMContentLoaded', refreshCart);

  // ═══════════════════════════════════════════════════════════
  // PREDICTIVE SEARCH — combobox dentro de [data-predictive-search]
  // (sections/search-modal.liquid). Al escribir (debounce 250 ms, mínimo
  // 2 letras) pide sections/predictive-search.liquid dos veces en paralelo:
  //   · routes.predictive_search_url → productos, colecciones y páginas
  //   · action del form (/search, con el prefijo de idioma) → total de
  //     productos para «Ver los N resultados»
  // Teclado: ↑ ↓ recorren [data-search-option] visibles (aria-activedescendant),
  // Enter abre la opción activa o manda el form, Esc cierra (dialog nativo).
  // Chips [data-search-term] escriben su término y buscan al momento.
  // ═══════════════════════════════════════════════════════════
  (function () {
    var MIN_CHARS = 2;
    var DEBOUNCE = 250;
    var timer = null;
    var seq = 0;
    var aborter = null;

    function parts(root) {
      return {
        root: root,
        form: root.querySelector('[data-search-form]'),
        input: root.querySelector('[data-search-input]'),
        clear: root.querySelector('[data-search-clear]'),
        results: root.querySelector('[data-search-results]'),
        initial: root.querySelector('[data-search-initial]'),
        message: root.querySelector('[data-search-message]'),
        status: root.querySelector('[data-search-status]')
      };
    }

    function visibleOptions(root) {
      return $$('[data-search-option]', root).filter(function (o) { return o.offsetParent !== null; });
    }

    function setActive(p, idx) {
      var opts = visibleOptions(p.root);
      opts.forEach(function (o) { o.classList.remove('is-active'); o.setAttribute('aria-selected', 'false'); });
      if (idx < 0 || idx >= opts.length) {
        p.input.removeAttribute('aria-activedescendant');
        return -1;
      }
      var o = opts[idx];
      if (!o.id) o.id = 'search-opt-' + Math.random().toString(36).slice(2, 9);
      o.classList.add('is-active');
      o.setAttribute('aria-selected', 'true');
      p.input.setAttribute('aria-activedescendant', o.id);
      o.scrollIntoView({ block: 'nearest' });
      return idx;
    }

    function activeIndex(p) {
      var id = p.input.getAttribute('aria-activedescendant');
      if (!id) return -1;
      return visibleOptions(p.root).findIndex(function (o) { return o.id === id; });
    }

    function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

    // Resalta cada palabra del término en los [data-search-hl] (sin innerHTML del usuario)
    function highlight(container, q) {
      var words = q.split(/\s+/).filter(function (w) { return w.length >= MIN_CHARS; }).map(escapeRe);
      if (!words.length) return;
      var re = new RegExp('(' + words.join('|') + ')', 'gi');
      $$('[data-search-hl]', container).forEach(function (el) {
        var text = el.textContent;
        var frag = document.createDocumentFragment();
        var last = 0;
        text.replace(re, function (m, g, i) {
          if (i > last) frag.appendChild(document.createTextNode(text.slice(last, i)));
          var mark = document.createElement('mark');
          mark.textContent = m;
          frag.appendChild(mark);
          last = i + m.length;
          return m;
        });
        if (last === 0) return;
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        el.textContent = '';
        el.appendChild(frag);
      });
    }

    function showInitial(p, show) {
      if (p.initial) p.initial.hidden = !show;
    }

    function reset(p) {
      clearTimeout(timer);
      if (aborter) aborter.abort();
      seq++;
      p.root.classList.remove('is-searching');
      p.results.innerHTML = '';
      if (p.message) p.message.innerHTML = '';
      if (p.status) p.status.textContent = '';
      showInitial(p, true);
      p.input.setAttribute('aria-expanded', 'false');
      setActive(p, -1);
    }

    function fetchSection(url, signal) {
      return fetch(url, { headers: { 'Accept': 'text/html' }, signal: signal })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-search-inner]');
        });
    }

    function applyCount(p, n) {
      var link = p.results.querySelector('[data-search-view-all]');
      if (!link || !(n > 0)) return;
      var label = link.querySelector('[data-search-view-all-label]');
      var text = n === 1 ? link.getAttribute('data-label-one') : (link.getAttribute('data-label-many') || '').replace('__N__', n);
      if (label && text) label.textContent = text;
    }

    function run(p, q) {
      var my = ++seq;
      if (aborter) aborter.abort();
      aborter = typeof AbortController === 'function' ? new AbortController() : null;
      var signal = aborter ? aborter.signal : undefined;
      var enc = encodeURIComponent(q);
      var suggestUrl = ((global.routes && global.routes.predictive_search_url) || '/search/suggest') +
        '?q=' + enc +
        '&resources%5Btype%5D=product,collection,page' +
        '&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each' +
        '&section_id=predictive-search';
      var searchUrl = (p.form.getAttribute('action') || '/search') +
        '?q=' + enc + '&type=product&options%5Bprefix%5D=last&section_id=predictive-search';

      p.root.classList.add('is-searching');
      p.results.setAttribute('aria-busy', 'true');

      var countPromise = fetchSection(searchUrl, signal)
        .then(function (inner) { return inner ? parseInt(inner.getAttribute('data-count'), 10) : NaN; })
        .catch(function () { return NaN; });

      fetchSection(suggestUrl, signal)
        .then(function (inner) {
          if (my !== seq) return;
          var empty = inner && inner.querySelector('[data-search-empty]');
          if (p.message) p.message.innerHTML = empty ? empty.outerHTML : '';
          if (empty) empty.remove();
          p.results.innerHTML = inner ? inner.innerHTML : '';
          highlight(p.results, q);
          var announce = p.results.querySelector('[data-search-announce]');
          if (p.status) p.status.textContent = empty ? empty.textContent.trim().replace(/\s+/g, ' ') : (announce ? announce.textContent : '');
          // Sin resultados: se vuelven a mostrar las búsquedas populares y lo más vendido
          showInitial(p, !!empty);
          p.input.setAttribute('aria-expanded', 'true');
          setActive(p, -1);
          return countPromise.then(function (n) { if (my === seq) applyCount(p, n); });
        })
        .catch(function (err) { if (err && err.name === 'AbortError') return; })
        .finally(function () {
          if (my !== seq) return;
          p.root.classList.remove('is-searching');
          p.results.removeAttribute('aria-busy');
        });
    }

    function onChange(p) {
      var q = p.input.value.trim();
      if (p.clear) p.clear.hidden = p.input.value.length === 0;
      clearTimeout(timer);
      if (q.length < MIN_CHARS) { reset(p); return; }
      timer = setTimeout(function () { run(p, q); }, DEBOUNCE);
    }

    document.addEventListener('input', function (e) {
      var input = e.target.closest('[data-search-input]');
      var root = input && input.closest('[data-predictive-search]');
      if (!root) return;
      onChange(parts(root));
    });

    document.addEventListener('keydown', function (e) {
      var input = e.target.closest && e.target.closest('[data-search-input]');
      var root = input && input.closest('[data-predictive-search]');
      if (!root) return;
      var p = parts(root);
      var opts = visibleOptions(root);
      var idx = activeIndex(p);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(p, idx + 1 >= opts.length ? 0 : idx + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(p, idx <= 0 ? (idx === 0 ? -1 : opts.length - 1) : idx - 1);
      } else if (e.key === 'Enter' && idx >= 0 && opts[idx]) {
        e.preventDefault();
        opts[idx].click();
      }
    });

    document.addEventListener('click', function (e) {
      var root = e.target.closest('[data-predictive-search]');
      if (!root) return;
      var p = parts(root);
      var chip = e.target.closest('[data-search-term]');
      if (chip) {
        e.preventDefault();
        p.input.value = chip.getAttribute('data-search-term');
        if (p.clear) p.clear.hidden = false;
        p.input.focus();
        clearTimeout(timer);
        run(p, p.input.value.trim());
        return;
      }
      if (e.target.closest('[data-search-clear]')) {
        e.preventDefault();
        p.input.value = '';
        p.clear.hidden = true;
        reset(p);
        p.input.focus();
      }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-search-form]');
      var input = form && form.querySelector('[data-search-input]');
      if (input && !input.value.trim()) { e.preventDefault(); input.focus(); }
    });

    // Al abrir el modal: si ya había texto, selecciónalo para reescribir rápido
    document.addEventListener('amatora:drawer:open', function (e) {
      var root = document.getElementById(e.detail && e.detail.id);
      if (!root || !root.hasAttribute('data-predictive-search')) return;
      var p = parts(root);
      setTimeout(function () { if (p.input.value) p.input.select(); }, 100);
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // MEDIA — Thumbnails de galería de producto cambian la imagen principal
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-media-thumb]');
    if (!thumb) return;
    e.preventDefault();
    var gallery = thumb.closest('[data-product-gallery]');
    if (!gallery) return;
    var idx = thumb.getAttribute('data-media-thumb');
    var main = gallery.querySelector('[data-media-main="' + idx + '"]');
    if (!main) return;
    gallery.querySelectorAll('[data-media-main]').forEach(function (m) { m.hidden = true; });
    gallery.querySelectorAll('[data-media-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
    main.hidden = false;
    thumb.classList.add('is-active');
  });

  // Expose
  global.AmatoraShop = {
    openDialog: openDialog,
    closeDialog: function (id) { var d = document.getElementById(id); if (d) closeDialog(d); },
    refreshCart: refreshCart,
    moneyFormat: moneyFormat,
    emit: emit
  };

})(typeof window !== 'undefined' ? window : this);


/* ───────── CUENTA REGRESIVA ([data-countdown], snippets/amatora-countdown.liquid) ─────────
   Se muestra solo entre data-start y data-end; un solo intervalo para todas. */
(function () {
  var pad = function (n) { return n < 10 ? '0' + n : String(n); };
  var timer = null;
  function tick() {
    var now = Date.now();
    var any = false;
    document.querySelectorAll('[data-countdown]').forEach(function (el) {
      var start = Date.parse(el.dataset.start || '') || 0;
      var end = Date.parse(el.dataset.end || '');
      if (!end || now < start || now >= end) { el.hidden = true; return; }
      any = true;
      el.hidden = false;
      var t = Math.floor((end - now) / 1000);
      var d = Math.floor(t / 86400), h = Math.floor(t % 86400 / 3600), m = Math.floor(t % 3600 / 60), sec = t % 60;
      var set = function (k, v) { var b = el.querySelector('[data-cd="' + k + '"]'); if (b && b.textContent !== v) b.textContent = v; };
      set('d', pad(d)); set('h', pad(h)); set('m', pad(m)); set('s', pad(sec));
    });
    if (!any && timer) { clearInterval(timer); timer = null; }
  }
  function start() { tick(); if (!timer) timer = setInterval(tick, 1000); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  // El carrito se vuelve a dibujar al agregar: volver a pintar los relojes nuevos
  document.addEventListener('cart:updated', start);
  document.addEventListener('shopify:section:load', start);
  new MutationObserver(function (ms) {
    for (var i = 0; i < ms.length; i++) for (var j = 0; j < ms[i].addedNodes.length; j++) {
      var n = ms[i].addedNodes[j];
      if (n.nodeType === 1 && (n.matches('[data-countdown]') || n.querySelector('[data-countdown]'))) { start(); return; }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* ───────── FICHA: al tocar un color, la galería en carrusel va a la foto de ese color ─────────
   No espera a que se elija talla (con tallas pendientes no hay variante completa). Usa la
   posición de la foto destacada de la primera variante con ese color. */
(function () {
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.matches || !input.matches('[data-product-form] [data-option-input]')) return;
    var label = input.closest('.amatora-variant__option--swatch');
    if (!label) return;
    var form = input.closest('[data-product-form]');
    var fs = input.closest('[data-option-position]');
    var json = form && form.querySelector('[data-variants-json]');
    if (!json || !fs) return;
    var variants; try { variants = JSON.parse(json.textContent); } catch (err) { return; }
    var idx = Number(fs.getAttribute('data-option-position')) - 1;
    var v = variants.find(function (x) { return x.options[idx] === input.value && x.featured_media; });
    if (!v) return;
    var section = form.closest('.main-product-am') || document;
    var slider = section.querySelector('.amatora-gallery--slider [data-amatora-slider]');
    var inst = slider && window.SliderAmatora && window.SliderAmatora.get && window.SliderAmatora.get(slider);
    if (inst && typeof inst._snap === 'function') inst._snap(v.featured_media.position - 1, true);
  });
})();
